import { Component, inject, Inject, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { DisableAutoPayClient, SettingsState } from '../state';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { extractFailureContext, LoadableStatus } from '@aiwyn/shared';
import { AutoPaymentPlanTrackService, AutoPaymentPlanWorkflowEvent } from '@aiwyn/common/payments';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AutoPay } from '@aiwyn/api';

@Component({
  selector: 'app-delete-auto-pay-dialog',
  templateUrl: './delete-auto-pay-dialog.component.html',
  styleUrls: ['./delete-auto-pay-dialog.component.scss'],
  standalone: false,
  providers: [AutoPaymentPlanTrackService],
})
export class DeleteAutoPayDialogComponent implements OnInit {
  private _unsubscribe$: Subject<void> = new Subject<void>();

  // Set to true once a terminal AUTO_PAYMENT_PLAN_* event has fired (DEACTIVATE_SUCCEEDED).
  // afterClosed reads this to decide whether to emit AUTO_PAYMENT_PLAN_CANCELLED.
  private _didEmitTerminalEvent = false;

  didDeleteError$: Observable<boolean> = this.store.select(SettingsState.getProcessingAutoPayDisable).pipe(
    map(status => status === LoadableStatus.ERROR)
  );

  isDeleteProcessing$: Observable<boolean> = this.store.select(SettingsState.getProcessingAutoPayDisable).pipe(
    map(status => status === LoadableStatus.LOADING)
  );

  private readonly autoPaymentPlanTrackService = inject(AutoPaymentPlanTrackService);

  constructor(
    public dialogRef: MatDialogRef<DeleteAutoPayDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AutoPay,
    private readonly store: Store,
  ) { }

  ngOnInit(): void {
    this.autoPaymentPlanTrackService.trackWorkflow(
      AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_SUBMITTED,
      { planId: String(this.data.autoPayId) },
    );

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!this._didEmitTerminalEvent) {
        this.autoPaymentPlanTrackService.trackWorkflow(AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CANCELLED);
      }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  disable(): void {
    const planId = String(this.data.autoPayId);
    const operationKey = 'AUTO_PAYMENT_PLAN_DEACTIVATE';
    this.autoPaymentPlanTrackService.startOperation(operationKey);

    this.store.dispatch(new DisableAutoPayClient(this.data.autoPayId)).pipe(
      take(1),
      takeUntil(this._unsubscribe$),
      tap({
        next: _ => {
          this._didEmitTerminalEvent = true;
          this.autoPaymentPlanTrackService.trackWorkflow(
            AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_SUCCEEDED,
            { planId, operationDurationMs: this.autoPaymentPlanTrackService.endOperation(operationKey) },
          );
          this.dialogRef.close();
        },
        error: error => this.autoPaymentPlanTrackService.trackWorkflow(
          AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_DEACTIVATE_FAILED,
          {
            ...extractFailureContext(error),
            planId,
            operationDurationMs: this.autoPaymentPlanTrackService.endOperation(operationKey),
          },
        ),
      }),
    ).subscribe();
  }
}
